import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment-timezone";
import {
	UTILITY_CONSUMPTION_DIRECTION,
	UTILITY_CONSUMPTION_MODE,
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionDirection,
	type UtilityConsumptionMode,
	type UtilityConsumptionPeriod,
	type UtilityConsumptionReportCalendarMode,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";

const moment = getMomentTz();

const getPlantDate = (referenceDate: Date): Moment => {
	if (Number.isNaN(referenceDate.getTime())) {
		throw new Error("Utility consumption reference date must be valid.");
	}
	return moment(referenceDate);
};

const getPeriodStart = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
): Moment => {
	const localDate = getPlantDate(referenceDate);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return localDate.startOf("month");
		case UTILITY_CONSUMPTION_MODE.WEEK:
			return localDate.startOf("day").day(0);
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return localDate.startOf("year");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return localDate.startOf("day");
	}
};

const getPeriodEnd = (mode: UtilityConsumptionMode, start: Moment): Moment => {
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return start.clone().add(1, "month");
		case UTILITY_CONSUMPTION_MODE.WEEK:
			return start.clone().add(1, "week");
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return start.clone().add(1, "year");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return start.clone().add(1, "day");
	}
};

export const getUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
): UtilityConsumptionPeriod => {
	const start = getPeriodStart(mode, referenceDate);
	const end = getPeriodEnd(mode, start);
	return {
		startUtc: start.clone().utc().toDate(),
		endUtc: end.utc().toDate(),
	};
};

export const getCurrentUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
): UtilityConsumptionPeriod => getUtilityConsumptionPeriod(mode, new Date());

export const moveUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
	direction: UtilityConsumptionDirection,
): Date => {
	const amount = direction === UTILITY_CONSUMPTION_DIRECTION.PREVIOUS ? -1 : 1;
	const nextDate = getPlantDate(referenceDate);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			nextDate.add(amount, "month");
			break;
		case UTILITY_CONSUMPTION_MODE.WEEK:
			nextDate.add(amount, "week");
			break;
		case UTILITY_CONSUMPTION_MODE.MONTH:
			nextDate.add(amount, "year");
			break;
		case UTILITY_CONSUMPTION_MODE.DATE:
			nextDate.add(amount, "day");
			break;
	}
	return nextDate.toDate();
};

/** Weeks run Sunday through Saturday (US convention), matching `getPeriodStart`. */
export const getUtilityConsumptionPeriodLabel = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
): string => {
	const start = moment(referenceDate);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return start.format("MMMM YYYY");
		case UTILITY_CONSUMPTION_MODE.WEEK: {
			const end = start.clone().add(6, "days");
			return `${start.format("MMM D")} - ${end.format(
				end.isSame(start, "month") ? "D, YYYY" : "MMM D, YYYY",
			)}`;
		}
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return start.format("YYYY");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return start.format("ddd, MMM D, YYYY");
	}
};

export const isUtilityConsumptionNextDisabled = (
	period: UtilityConsumptionPeriod,
): boolean => {
	const today = moment().startOf("day");
	const start = moment(period.startUtc);
	const end = moment(period.endUtc);
	return start.isSameOrBefore(today) && end.isAfter(today);
};

const REPORT_DATE_FORMAT = "YYYY-MM-DD";

export const toUtilityConsumptionDateKey = (value: Date): string =>
	moment(value).format(REPORT_DATE_FORMAT);

export const fromUtilityConsumptionDateKey = (
	dateKey: string | null,
	fallback: () => Date,
): Date => {
	if (dateKey === null) return fallback();
	const parsed = moment(dateKey, REPORT_DATE_FORMAT, true);
	return parsed.isValid() ? parsed.startOf("day").toDate() : fallback();
};

const getReportDate = (dateKey: string): Moment => {
	const date = moment(dateKey, REPORT_DATE_FORMAT, true);
	if (!date.isValid()) {
		throw new Error("Report dates must use YYYY-MM-DD format.");
	}
	return date.startOf("day");
};

export const getDefaultUtilityConsumptionReportRange =
	(): UtilityConsumptionReportRange => {
		const today = moment().startOf("day");
		return {
			startDateKey: today
				.clone()
				.subtract(7, "days")
				.format(REPORT_DATE_FORMAT),
			endDateKey: today.clone().subtract(1, "day").format(REPORT_DATE_FORMAT),
		};
	};

export const getDefaultUtilityConsumptionSectionRange =
	(): UtilityConsumptionReportRange => {
		const today = moment().startOf("day");
		return {
			startDateKey: today.clone().startOf("year").format(REPORT_DATE_FORMAT),
			endDateKey: today.format(REPORT_DATE_FORMAT),
		};
	};

export const getUtilityConsumptionReportDate = (dateKey: string): Moment =>
	getReportDate(dateKey);

export const getUtilityConsumptionReportDateError = (
	range: UtilityConsumptionReportRange,
): string | null => {
	try {
		getReportDate(range.startDateKey);
		getReportDate(range.endDateKey);
	} catch {
		return "Start and End must be valid dates.";
	}
	return null;
};

const reportCalendarDashboardModes: Record<
	UtilityConsumptionReportCalendarMode,
	UtilityConsumptionMode
> = {
	day: UTILITY_CONSUMPTION_MODE.DATE,
	week: UTILITY_CONSUMPTION_MODE.WEEK,
	month: UTILITY_CONSUMPTION_MODE.DAY,
	year: UTILITY_CONSUMPTION_MODE.MONTH,
};

const getUtilityConsumptionReportCalendarPeriod = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
): UtilityConsumptionPeriod =>
	getUtilityConsumptionPeriod(
		reportCalendarDashboardModes[mode],
		referenceDate,
	);

export const getUtilityConsumptionReportPeriod = (
	mode: UtilityConsumptionReportMode,
	referenceDate: Date,
	range: UtilityConsumptionReportRange,
): UtilityConsumptionPeriod => {
	if (
		mode !== UTILITY_CONSUMPTION_REPORT_MODE.PERIOD &&
		mode !== UTILITY_CONSUMPTION_REPORT_MODE.SECTION
	) {
		return getUtilityConsumptionReportCalendarPeriod(mode, referenceDate);
	}

	const start = getReportDate(range.startDateKey);
	let end = getReportDate(range.endDateKey).add(1, "day");
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION) {
		const boundary = getPlantDate(referenceDate);
		if (end.isAfter(boundary)) end = boundary;
	}
	return {
		startUtc: start.clone().utc().toDate(),
		endUtc: end.utc().toDate(),
	};
};

export const moveUtilityConsumptionReportDate = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
	direction: UtilityConsumptionDirection,
): Date => {
	return moveUtilityConsumptionPeriod(
		reportCalendarDashboardModes[mode],
		referenceDate,
		direction,
	);
};

export const getUtilityConsumptionReportPeriodLabel = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
): string =>
	getUtilityConsumptionPeriodLabel(
		reportCalendarDashboardModes[mode],
		referenceDate,
	);

export const getUtilityConsumptionReportBucketLabels = (
	starts: Date[],
	mode: UtilityConsumptionReportMode,
): string[] => {
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.YEAR) {
		return starts.map((start) => moment(start).format("MMM"));
	}

	if (mode !== UTILITY_CONSUMPTION_REPORT_MODE.DAY) {
		return starts.map((start) => moment(start).format("MM/DD/YYYY"));
	}

	const localTimes = starts.map((start) => moment(start).format("HH:mm"));
	return starts.map((start, index) => {
		const localTime = localTimes[index];
		const repeated =
			localTimes.indexOf(localTime) !== localTimes.lastIndexOf(localTime);
		return repeated ? `${localTime} ${moment(start).format("Z")}` : localTime;
	});
};
